import { Anchor, Box, Button, Center, Group, Loader, Modal, Paper, SegmentedControl, Stack, Text } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { Link } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryOptions } from "@/Services";
import { queryAdminDesignBrief } from "@/Services/studio-design-request";
import { formatName } from "./formats";

const TAB_FEEDBACK = 'feedback';
const TAB_BRIEF = 'brief';

const Row = ({ label, children }) => (
    <Group gap="md" align="flex-start" wrap="nowrap" py={8} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Text fz={12} c="dimmed" w={110} style={{ flexShrink: 0 }}>{label}</Text>
        <Box fz={13} style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
    </Group>
);

const UrlLink = ({ href }) => (href
    ? <Anchor href={href} target="_blank" rel="noopener noreferrer" fz={13}>{href}</Anchor>
    : <Text span fz={13} c="dimmed">—</Text>);

const Files = ({ files }) => {
    const { t } = useTranslation();

    if (!files?.length) {
        return <Text span fz={13} c="dimmed">{t('designRequests.brief.noFiles')}</Text>;
    }

    return (
        <Stack gap={4}>
            {files.map((file) => (
                <Group key={file.id} justify="space-between" wrap="nowrap" gap="sm">
                    <Text fz={13} truncate="end">{file.name}</Text>
                    <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Anchor href={file.url} target="_blank" rel="noopener noreferrer" fz={13}>{t('designRequests.brief.preview')}</Anchor>
                        <Anchor href={file.url} download={file.name} fz={13}>{t('designRequests.brief.download')}</Anchor>
                    </Group>
                </Group>
            ))}
        </Stack>
    );
};

/**
 * AD-222: "Design Brief of {campaign}" — what the customer briefed, and, once they
 * asked for changes, their feedback per round (the Feedback tab opens first).
 * `inEditor`: opened from "Check Design Brief", so there is no "Start to Design".
 */
export const DesignBriefModal = ({ designRequestId, opened, onClose, inEditor = false }) => {

    const { t } = useTranslation();
    const query = queryAdminDesignBrief(designRequestId, { enabled: Boolean(opened && designRequestId) });
    const languages = queryOptions({ type: 'language' });
    const request = query.data;
    const hasFeedback = (request?.feedback?.length || 0) > 0;
    const [tab, setTab] = useState(TAB_BRIEF);

    useEffect(() => {
        if (opened && request) {
            setTab(hasFeedback ? TAB_FEEDBACK : TAB_BRIEF);
        }
    }, [opened, request?.id, hasFeedback]);

    const brief = request?.brief;
    const languageName = (code) => (languages.data || []).find((option) => String(option.value) === String(code))?.label || code;

    const briefView = brief && (
        <Paper withBorder radius="md" px="md" py={4}>
            <Row label={t('designRequests.brief.advertiser')}>{brief.advertiser_name}</Row>
            <Row label={t('designRequests.brief.campaign')}>{brief.campaign_name || '—'}</Row>
            <Row label={t('designRequests.brief.destination')}>
                {brief.destination_type === 'clickout_url'
                    ? <>{t('designRequests.brief.website')} – <UrlLink href={brief.clickout_url} /></>
                    : t('designRequests.brief.instant')}
            </Row>
            <Row label={t('designRequests.brief.linkContent')}><UrlLink href={brief.content_link} /></Row>
            <Row label={t('designRequests.brief.formats')}>{(brief.formats || []).map(formatName).join(', ') || '—'}</Row>
            <Row label={t('designRequests.brief.language')}>{languageName(brief.language) || '—'}</Row>
            <Row label={t('designRequests.brief.promote')}>
                <Text fz={13} style={{ whiteSpace: 'pre-line' }}>{brief.promote_text || '—'}</Text>
            </Row>
            <Row label={t('designRequests.brief.assets')}><Files files={brief.files} /></Row>
            <Box py={8}>
                <Group gap="md" wrap="nowrap">
                    <Text fz={12} c="dimmed" w={110}>{t('designRequests.brief.round')}</Text>
                    <Text fz={13}>
                        {request.round > 1
                            ? t('designRequests.brief.roundN', { round: request.round, count: request.round - 1 })
                            : t('designRequests.brief.roundFirst')}
                    </Text>
                </Group>
            </Box>
        </Paper>
    );

    const feedbackView = hasFeedback && (
        <Stack gap="sm">
            {request.feedback.map((round) => (
                <Paper key={round.round} withBorder radius="md" px="md" py={4}>
                    <Row label={t('designRequests.brief.round')}>
                        {t('designRequests.brief.feedbackRound', { number: round.round - 1 })}
                        {round.created_at && (
                            <Text span fz={12} c="dimmed"> · {new Date(round.created_at).toLocaleString()}</Text>
                        )}
                    </Row>
                    <Row label={t('designRequests.brief.feedback')}>
                        <Text fz={13} style={{ whiteSpace: 'pre-line' }}>{round.text}</Text>
                    </Row>
                    <Row label={t('designRequests.brief.link')}><UrlLink href={round.link} /></Row>
                    <Box py={8}>
                        <Group gap="md" align="flex-start" wrap="nowrap">
                            <Text fz={12} c="dimmed" w={110} style={{ flexShrink: 0 }}>{t('designRequests.brief.assets')}</Text>
                            <Box style={{ flex: 1, minWidth: 0 }}><Files files={round.files} /></Box>
                        </Group>
                    </Box>
                </Paper>
            ))}
        </Stack>
    );

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size="min(640px, 95vw)"
            radius={16}
            title={<Text fw={600} fz={16}>{t('designRequests.brief.title', { campaign: brief?.campaign_name || brief?.advertiser_name || '' })}</Text>}
        >
            {query.isLoading && <Center py="xl"><Loader size="sm" /></Center>}
            {query.isError && <Text c="red" fz={14}>{t('designRequests.brief.loadFailed')}</Text>}

            {request && (
                <Stack gap="md">
                    {hasFeedback && (
                        <SegmentedControl
                            size="xs"
                            w="fit-content"
                            value={tab}
                            onChange={setTab}
                            data={[
                                { value: TAB_FEEDBACK, label: t('designRequests.brief.feedbackTab') },
                                { value: TAB_BRIEF, label: t('designRequests.brief.briefTab') },
                            ]}
                        />
                    )}

                    {tab === TAB_FEEDBACK && hasFeedback ? feedbackView : briefView}

                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={onClose}>{t('common.close')}</Button>
                        {!inEditor && (
                            <Button
                                component={Link}
                                href={route('studio.ad_builder.edit', { ad: request.ad_id })}
                                rightSection={<IconExternalLink size={16} />}
                            >
                                {t('designRequests.brief.startDesign')}
                            </Button>
                        )}
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};
